import { useState } from 'react'
import { css } from '../lib/css'
import { Hover } from '../lib/Hover'
import { SITE } from '../lib/config'
import { useT } from '../i18n'

// Supporter signup. When SITE.NEWSLETTER_ACTION is set (MailerLite classic-embed
// subscribe URL) it POSTs the fields to MailerLite and shows an inline thank-you;
// otherwise it composes a mailto — never a dead form. Keeps our own design rather
// than MailerLite's default form. Fields map to MailerLite's `fields[...]` keys.
const INTERESTS = ['Donor', 'Volunteer', 'Referral organization', 'Community partner', 'General supporter']
const FIELD = `height: 48px; width: 100%; border: none; border-radius: 6px; padding: 0 16px; font-size: 15px; color: #16233c; background: #eafffb; outline: none`

export default function Newsletter() {
  const t = useT()
  const action = SITE.NEWSLETTER_ACTION
  const [f, setF] = useState({ first: '', last: '', email: '', interest: '' })
  const [sent, setSent] = useState(false)
  const on = (k: keyof typeof f) => (e: { target: { value: string } }) => setF((s) => ({ ...s, [k]: e.target.value }))

  const submit = (e: React.FormEvent) => {
    e.preventDefault()
    if (action) {
      const body = new URLSearchParams({
        'fields[email]': f.email,
        'fields[name]': f.first,
        'fields[last_name]': f.last,
        'fields[interest]': f.interest,
        'ml-submit': '1',
        anticsrf: 'true',
      })
      // Opaque (no-cors) POST straight to MailerLite's subscribe endpoint.
      fetch(action, { method: 'POST', mode: 'no-cors', body }).catch(() => {})
      setSent(true)
      return
    }
    const mail = [
      `First name: ${f.first}`,
      `Last name: ${f.last}`,
      `Email: ${f.email}`,
      `Area of interest: ${f.interest || 'not provided'}`,
    ].join('\n')
    window.location.href = `mailto:${SITE.ORG_EMAIL}?subject=${encodeURIComponent('Supporter signup')}&body=${encodeURIComponent(mail)}`
  }

  return (
    <div
      data-reveal="1"
      style={css(
        `background: #16a394; color: #eafffb; padding: 72px clamp(20px, calc((100% - 1240px) / 2), 480px); display: grid; grid-template-columns: 1fr 1fr; gap: 40px; align-items: center`,
      )}
    >
      <div style={css(`display: flex; flex-direction: column; gap: 12px`)}>
        <div style={css(`font-family: 'JetBrains Mono', monospace; font-size: 11px; letter-spacing: .14em; text-transform: uppercase; color: #bff3ea`)}>{t(`Stay close to the work`)}</div>
        <h2 style={css(`margin: 0; font-family: Newsreader, Georgia, serif; font-size: clamp(28px, 3vw, 40px); line-height: 1.1; font-weight: 400; letter-spacing: -.02em`)}>{t(`Follow the work as we launch.`)}</h2>
        <p style={css(`margin: 0; font-size: 16px; line-height: 1.6; color: #d6fff7; max-width: 48ch`)}>{t(`Occasional updates on our progress and ways to get involved. No spam. Unsubscribe anytime.`)}</p>
      </div>
      {sent ? (
        <div style={css(`display: flex; flex-direction: column; gap: 10px`)}>
          <div style={css(`font-family: Newsreader, Georgia, serif; font-size: clamp(26px, 3vw, 34px); line-height: 1.15`)}>{t(`Thank you!`)}</div>
          <p style={css(`margin: 0; font-size: 16px; line-height: 1.6; color: #d6fff7; max-width: 42ch`)}>{t(`You're on the list. Please check your inbox to confirm your subscription.`)}</p>
        </div>
      ) : (
        <form onSubmit={submit} style={css(`display: flex; flex-direction: column; gap: 12px`)}>
          <div data-mq-2col="1" style={css(`display: grid; grid-template-columns: 1fr 1fr; gap: 12px`)}>
            <input value={f.first} onChange={on('first')} placeholder={t('First name')} aria-label={t('First name')} style={css(FIELD)} />
            <input value={f.last} onChange={on('last')} placeholder={t('Last name')} aria-label={t('Last name')} style={css(FIELD)} />
          </div>
          <input type="email" required value={f.email} onChange={on('email')} placeholder={t('Email address')} aria-label={t('Email address')} style={css(FIELD)} />
          <select
            value={f.interest}
            onChange={on('interest')}
            aria-label={t('Area of interest')}
            style={css(`${FIELD}; color: ${f.interest ? '#16233c' : '#556d7b'}; cursor: pointer`)}
          >
            <option value="">{t('Area of interest')}</option>
            {INTERESTS.map((i) => (
              <option key={i} value={i} style={css(`color: #16233c`)}>
                {t(i)}
              </option>
            ))}
          </select>
          <Hover
            as="button"
            type="submit"
            style={css(`height: 50px; border: none; border-radius: 999px; padding: 0 30px; font-size: 15px; font-weight: 600; background: #16233c; color: #eafffb; cursor: pointer; align-self: flex-start`)}
            hoverStyle={css(`background: #0f1a2e`)}
          >
            {t('Subscribe')}
          </Hover>
        </form>
      )}
    </div>
  )
}
